import * as THREE from 'three';

/**
 * Wax-print (ankara) fabrics, generated: bold repeating motifs in saturated colours. Applied to a
 * garment by multiplying the print over a greyscale copy of the garment's own texture, so the
 * folds, seams and shading the garment was authored with still show through the pattern.
 */

export interface PrintDef { id: string; label: string; bg: string; ink: string[]; motif: 'sun' | 'leaf' | 'wave' | 'eye' | 'diamond' | 'solid' | 'sequin' | 'cloud' | 'stripe' | 'damask' | 'plaid' }

export const PRINTS: PrintDef[] = [
  { id: 'ankara-sun', label: 'Sunburst', bg: '#e8742a', ink: ['#1d3f8a', '#f6c445', '#ffffff'], motif: 'sun' },
  { id: 'ankara-leaf', label: 'Palm leaf', bg: '#1f7a4d', ink: ['#d6338a', '#ffd23f', '#f5f0e1'], motif: 'leaf' },
  { id: 'ankara-wave', label: 'Lagoon', bg: '#22265e', ink: ['#e2a83a', '#d93a2b', '#7fd1c7'], motif: 'wave' },
  { id: 'ankara-eye', label: 'Peacock', bg: '#0f6c7a', ink: ['#f2b134', '#8b2a8f', '#ffffff'], motif: 'eye' },
  { id: 'ankara-diamond', label: 'Kente-ish', bg: '#b8272c', ink: ['#f2c12e', '#14652f', '#111111'], motif: 'diamond' },
  // going out: plain colours and sequins
  { id: 'solid-black', label: 'Black', bg: '#1b1a1c', ink: [], motif: 'solid' },
  { id: 'solid-red', label: 'Red', bg: '#b3122a', ink: [], motif: 'solid' },
  { id: 'solid-white', label: 'White', bg: '#f2f0ec', ink: [], motif: 'solid' },
  { id: 'solid-brown', label: 'Brown', bg: '#5a4330', ink: [], motif: 'solid' },
  { id: 'solid-hotpink', label: 'Hot pink', bg: '#e0307e', ink: [], motif: 'solid' },
  // (the aesthetics' plain colours: earthy, clean, soft, old money)
  { id: 'solid-terracotta', label: 'Terracotta', bg: '#b0532e', ink: [], motif: 'solid' },
  { id: 'solid-olive', label: 'Olive', bg: '#5e6232', ink: [], motif: 'solid' },
  { id: 'solid-cream', label: 'Cream', bg: '#ece2cc', ink: [], motif: 'solid' },
  { id: 'solid-camel', label: 'Camel', bg: '#b08a5a', ink: [], motif: 'solid' },
  { id: 'solid-beige', label: 'Beige', bg: '#d6c4a6', ink: [], motif: 'solid' },
  { id: 'solid-blush', label: 'Blush', bg: '#e8b4b8', ink: [], motif: 'solid' },
  { id: 'solid-lilac', label: 'Lilac', bg: '#b9a6d6', ink: [], motif: 'solid' },
  { id: 'solid-navy', label: 'Navy', bg: '#1e2a48', ink: [], motif: 'solid' },
  { id: 'solid-sage', label: 'Sage', bg: '#9aab8a', ink: [], motif: 'solid' },
  { id: 'solid-rust', label: 'Rust', bg: '#8a3a1c', ink: [], motif: 'solid' },
  { id: 'solid-grey', label: 'Grey', bg: '#7a7a7e', ink: [], motif: 'solid' },
  { id: 'solid-lime', label: 'Lime', bg: '#a8c82a', ink: [], motif: 'solid' },
  { id: 'sequin-gold', label: 'Gold sequins', bg: '#a8801f', ink: ['#fff3b0', '#ffd34d', '#6b4e0e'], motif: 'sequin' },
  { id: 'sequin-silver', label: 'Silver sequins', bg: '#8a8d94', ink: ['#ffffff', '#d8dce4', '#3a3c42'], motif: 'sequin' },
  { id: 'sequin-black', label: 'Black sequins', bg: '#141317', ink: ['#8a8a96', '#4a4a56', '#000000'], motif: 'sequin' },
  // the clothes of a country: Chinese silks (qipao, tang jacket), Lagos agbada damask and aso-oke, a winter plaid
  { id: 'silk-red', label: 'Red silk', bg: '#a8121f', ink: ['#e8b54a', '#f5d98a'], motif: 'cloud' },
  { id: 'silk-jade', label: 'Jade silk', bg: '#1f6b5a', ink: ['#d9c27a', '#f2e6b8'], motif: 'cloud' },
  { id: 'silk-navy', label: 'Navy silk', bg: '#1c2346', ink: ['#c9a75a', '#e8d6a0'], motif: 'cloud' },
  { id: 'damask-white', label: 'White damask', bg: '#f1ede4', ink: ['#dcd5c6', '#c9bfac'], motif: 'damask' },
  { id: 'damask-sky', label: 'Sky damask', bg: '#8fb4d8', ink: ['#7aa2c9', '#b5cfe8'], motif: 'damask' },
  { id: 'asooke-indigo', label: 'Indigo aso-oke', bg: '#1d2550', ink: ['#c9a227', '#3a4a8a', '#e8e0c8'], motif: 'stripe' },
  { id: 'asooke-wine', label: 'Wine aso-oke', bg: '#5a1424', ink: ['#d4a32a', '#8a2a3a', '#f0e2c4'], motif: 'stripe' },
  { id: 'plaid-camel', label: 'Camel plaid', bg: '#b8895a', ink: ['#5a3a22', '#e8d4b0', '#a3202c'], motif: 'plaid' },
  { id: 'kimono-indigo', label: 'Indigo kimono', bg: '#1d2a5a', ink: ['#f2f2f2', '#8aa6d8'], motif: 'cloud' },
  { id: 'kimono-sakura', label: 'Sakura kimono', bg: '#f2c6cf', ink: ['#d9667a', '#ffffff'], motif: 'leaf' },
];

function drawPrint(d: PrintDef, size = 512): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  g.fillStyle = d.bg;
  g.fillRect(0, 0, size, size);
  if (d.motif === 'solid') return c;
  if (d.motif === 'sequin') {
    // little discs catching the light at random: a few bright, most mid, some in shadow
    for (let k = 0; k < size * size / 40; k++) {
      g.fillStyle = d.ink[Math.random() < 0.18 ? 0 : Math.random() < 0.7 ? 1 : 2];
      g.beginPath(); g.arc(Math.random() * size, Math.random() * size, 1.6 + Math.random() * 1.6, 0, Math.PI * 2); g.fill();
    }
    return c;
  }
  if (d.motif === 'stripe') {
    // aso-oke: hand-woven strips, narrow bands of colour and a metallic thread
    for (let y = 0, k = 0; y < size; k++) { const h = [26, 8, 4, 8][k % 4]; g.fillStyle = k % 4 === 0 ? d.bg : d.ink[k % d.ink.length]; g.fillRect(0, y, size, h); y += h; }
    for (let x = 0; x < size; x += 6) { g.fillStyle = 'rgba(0,0,0,.08)'; g.fillRect(x, 0, 1, size); }
    return c;
  }
  if (d.motif === 'plaid') {
    for (const [col, w, step] of [[d.ink[0], 40, 128], [d.ink[1], 10, 128], [d.ink[2], 4, 64]] as const) {
      g.fillStyle = col; g.globalAlpha = 0.55;
      for (let p = 0; p < size; p += step) { g.fillRect(p, 0, w, size); g.fillRect(0, p, size, w); }
    }
    g.globalAlpha = 1;
    return c;
  }
  const cell = size / 4;
  for (let j = 0; j < 4; j++) for (let i = 0; i < 4; i++) {
    const cx = (i + 0.5 + (j % 2) * 0.5) * cell, cy = (j + 0.5) * cell;
    g.save();
    g.translate(cx % size, cy);
    switch (d.motif) {
      case 'sun':
        for (let r = cell * 0.42, k = 0; r > 4; r -= cell * 0.09, k++) { g.beginPath(); g.arc(0, 0, r, 0, Math.PI * 2); g.fillStyle = d.ink[k % d.ink.length]; g.fill(); }
        for (let a = 0; a < 12; a++) { g.rotate(Math.PI / 6); g.fillStyle = d.ink[1]; g.fillRect(cell * 0.45, -3, cell * 0.1, 6); }
        break;
      case 'leaf':
        for (let a = 0; a < 5; a++) {
          g.rotate((Math.PI * 2) / 5);
          g.beginPath(); g.ellipse(cell * 0.2, 0, cell * 0.22, cell * 0.08, 0, 0, Math.PI * 2); g.fillStyle = d.ink[a % 2]; g.fill();
          g.strokeStyle = d.ink[2]; g.lineWidth = 2; g.beginPath(); g.moveTo(0, 0); g.lineTo(cell * 0.42, 0); g.stroke();
        }
        break;
      case 'wave':
        g.lineWidth = cell * 0.08;
        for (let k = 0; k < 3; k++) {
          g.strokeStyle = d.ink[k];
          g.beginPath();
          for (let x = -cell / 2; x <= cell / 2; x += 4) g.lineTo(x, (k - 1) * cell * 0.22 + Math.sin(x / (cell * 0.12)) * cell * 0.07);
          g.stroke();
        }
        g.fillStyle = d.ink[1]; g.beginPath(); g.arc(cell * 0.35, cell * 0.35, cell * 0.06, 0, Math.PI * 2); g.fill();
        break;
      case 'eye':
        g.beginPath(); g.ellipse(0, 0, cell * 0.4, cell * 0.26, 0, 0, Math.PI * 2); g.fillStyle = d.ink[0]; g.fill();
        g.beginPath(); g.arc(0, 0, cell * 0.17, 0, Math.PI * 2); g.fillStyle = d.ink[1]; g.fill();
        g.beginPath(); g.arc(0, 0, cell * 0.07, 0, Math.PI * 2); g.fillStyle = d.ink[2]; g.fill();
        break;
      case 'cloud':
        // auspicious clouds: curling scrolls in gold thread
        g.lineWidth = cell * 0.035; g.strokeStyle = d.ink[0];
        for (const [x, y, r] of [[-0.12, 0, 0.13], [0.1, -0.04, 0.1], [0.02, 0.1, 0.08]] as const) { g.beginPath(); g.arc(x * cell, y * cell, r * cell, 0.3, Math.PI * 1.7); g.stroke(); g.beginPath(); g.arc(x * cell, y * cell, r * cell * 0.45, 0, Math.PI * 1.4); g.stroke(); }
        g.fillStyle = d.ink[1]; g.beginPath(); g.arc(cell * 0.32, cell * 0.3, cell * 0.03, 0, Math.PI * 2); g.fill();
        break;
      case 'damask':
        // tone-on-tone brocade: a mirrored floral medallion, barely darker than the cloth
        g.fillStyle = d.ink[0];
        for (let a = 0; a < 8; a++) { g.rotate(Math.PI / 4); g.beginPath(); g.ellipse(cell * 0.16, 0, cell * 0.16, cell * 0.05, 0, 0, Math.PI * 2); g.fill(); }
        g.fillStyle = d.ink[1]; g.beginPath(); g.arc(0, 0, cell * 0.07, 0, Math.PI * 2); g.fill();
        break;
      case 'diamond':
        g.rotate(Math.PI / 4);
        g.fillStyle = d.ink[0]; g.fillRect(-cell * 0.3, -cell * 0.3, cell * 0.6, cell * 0.6);
        g.fillStyle = d.ink[1]; g.fillRect(-cell * 0.18, -cell * 0.18, cell * 0.36, cell * 0.36);
        g.fillStyle = d.ink[2]; g.fillRect(-cell * 0.06, -cell * 0.06, cell * 0.12, cell * 0.12);
        break;
    }
    g.restore();
  }
  return c;
}

const cache = new Map<string, Promise<THREE.Texture>>();

/** The garment texture with a print laid over it (the garment's own shading kept). */
export function printedTexture(baseUrl: string | null, printId: string): Promise<THREE.Texture> {
  const key = `${baseUrl}|${printId}`;
  let p = cache.get(key);
  if (p) return p;
  const def = PRINTS.find((x) => x.id === printId) ?? PRINTS[0];
  p = (async () => {
    const size = 1024;
    const out = document.createElement('canvas');
    out.width = out.height = size;
    const g = out.getContext('2d')!;
    // the garment's shading, as light greyscale
    if (baseUrl) {
      const img = await new Promise<HTMLImageElement | null>((res) => { const i = new Image(); i.crossOrigin = 'anonymous'; i.onload = () => res(i); i.onerror = () => res(null); i.src = baseUrl; });
      if (img) {
        g.drawImage(img, 0, 0, size, size);
        const px = g.getImageData(0, 0, size, size);
        // (a plain colour: the garment's shading around its own average, so a white shirt made from a dark one is white, not grey)
        let avg = 0;
        if (def.motif === 'solid') { let n = 0; for (let k = 0; k < px.data.length; k += 16) { const l = 0.3 * px.data[k] + 0.59 * px.data[k + 1] + 0.11 * px.data[k + 2]; if (l > 10) { avg += l; n++; } } avg = n ? avg / n : 0; }
        for (let k = 0; k < px.data.length; k += 4) {
          const l = 0.3 * px.data[k] + 0.59 * px.data[k + 1] + 0.11 * px.data[k + 2];
          const v = avg ? Math.min(255, 150 + (l / avg) * 88) : Math.min(255, 90 + l * 0.75); // lift the shadows so the print stays bright
          px.data[k] = px.data[k + 1] = px.data[k + 2] = v;
        }
        g.putImageData(px, 0, 0);
      } else { g.fillStyle = '#e0e0e0'; g.fillRect(0, 0, size, size); }
    } else { g.fillStyle = '#e0e0e0'; g.fillRect(0, 0, size, size); }
    // the print, tiled, multiplied over the shading
    g.globalCompositeOperation = 'multiply';
    const tile = drawPrint(def);
    for (let y = 0; y < size; y += 256) for (let x = 0; x < size; x += 256) g.drawImage(tile, x, y, 256, 256);
    g.globalCompositeOperation = 'source-over';
    const t = new THREE.CanvasTexture(out);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 8;
    return t;
  })();
  cache.set(key, p);
  return p;
}

/** A small swatch image of a print (for menus). */
export function printSwatch(printId: string): string {
  const def = PRINTS.find((x) => x.id === printId) ?? PRINTS[0];
  return drawPrint(def, 128).toDataURL();
}

const plain = new Map<string, THREE.CanvasTexture>();
/** A print on its own (no garment shading under it): for accessories made in code. */
export function printTexture(printId: string): THREE.CanvasTexture | null {
  const def = PRINTS.find((x) => x.id === printId);
  if (!def) return null;
  let t = plain.get(printId);
  if (!t) { t = new THREE.CanvasTexture(drawPrint(def, 256)); t.colorSpace = THREE.SRGBColorSpace; plain.set(printId, t); }
  return t.clone();
}
